import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { clipboard } from '../platform'

export interface RoomCodeBoxProps {
  readonly roomCode: string
  readonly shareLink: string
  readonly onCopied?: () => void
}

const FEEDBACK_MS = 1500

export function RoomCodeBox({ roomCode, shareLink, onCopied }: RoomCodeBoxProps): ReactElement {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )

  const scheduleReset = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      timer.current = null
      setState('idle')
    }, FEEDBACK_MS)
  }

  const copy = () => {
    void clipboard.writeText(shareLink).then(
      () => {
        setState('copied')
        scheduleReset()
        onCopied?.()
      },
      () => {
        setState('failed')
        scheduleReset()
      },
    )
  }

  return (
    <div className="mt-3">
      <p className="text-sm">
        房间号：
        <code className="ml-1 font-mono text-xl tracking-[2px] select-all text-accent">{roomCode}</code>
      </p>
      <div className="mt-2 flex items-stretch gap-2">
        <input
          // text-base + min-h-11：见 ChatPanel 的说明（防 iOS 聚焦缩放 + 触控尺寸）
          className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/20 bg-black/30 px-3 py-2 text-base text-white/80 md:min-h-0 md:text-sm"
          readOnly
          value={shareLink}
          aria-label="房间链接"
          onFocus={(event) => event.currentTarget.select()}
        />
        <button
          type="button"
          onClick={copy}
          className="shrink-0 rounded-lg border border-white/25 px-3 py-2 text-sm whitespace-nowrap text-white/90 hover:bg-white/10"
        >
          {state === 'copied' ? '已复制' : state === 'failed' ? '复制失败' : '复制链接'}
        </button>
      </div>
    </div>
  )
}
